import React, { ReactNode } from "react";
import { Helmet } from "react-helmet-async";
import { motion, Variants } from "framer-motion";
import { useInView } from "react-intersection-observer";
import HomeSlider from "./components/HomeSlider";
import StatsSection from "./components/StatsSection";
import AboutSummarySection from "./components/AboutSummarySection";
import BrandsSection from "./components/BrandsSection";
import BusinessStartSection from "./components/BusinessStartSection";
import GlobalPresence from "./components/GlobalPresence";
import ShowcaseSection from "./components/ShowcaseSection";
import FeaturedArticlesSection from "./components/FeaturedArticlesSection";
import SubCompaniesSection from "./components/SubCompaniesSection";
// Animation variants for different sections
const fadeInUp: Variants = {
  hidden: { opacity: 0, y: 60 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

const fadeInLeft: Variants = {
  hidden: { opacity: 0, x: 0 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      // ease: "easeOut"
    },
  },
};

const fadeInRight: Variants = {
  hidden: { opacity: 0, x: 60 },
  visible: {
    opacity: 1,
    x: 0,
    transition: {
      duration: 0.6,
      ease: "easeOut",
    },
  },
};

interface AnimatedSectionProps {
  children: ReactNode;
  animation?: Variants;
  className?: string;
}

// Animated section wrapper component
const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  children,
  animation = fadeInUp,
  className = "",
}) => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={inView ? "visible" : "hidden"}
      variants={animation}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const Home: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>
          VEA Group - Multi-Industry Ecosystem for Business
          Growth & Sustainability
        </title>
        <meta
          name="description"
          content="VEA Group is your trusted partner for e-commerce, digital marketing, and business growth. Discover our multi-industry ecosystem and innovative solutions."
        />
        <meta
          name="keywords"
          content="VEA Group, e-commerce, digital marketing, business growth, multi-industry, Vietnam"
        />
        <link
          rel="canonical"
          href="https://veagroup.global/"
        />
        <link rel="icon" href="/favicon.ico" />
        <link rel="manifest" href="/manifest.json" />
        {/* Open Graph */}
        <meta
          property="og:title"
          content="VEA Group - Multi-Industry Ecosystem for Business Growth & Sustainability"
        />
        <meta
          property="og:description"
          content="VEA Group is your trusted partner for e-commerce, digital marketing, and business growth. Discover our multi-industry ecosystem and innovative solutions."
        />
        <meta property="og:type" content="website" />
        <meta
          property="og:url"
          content="https://veagroup.global/"
        />
        <meta property="og:image" content="/logo.png" />
        {/* Twitter Card */}
        <meta
          name="twitter:card"
          content="summary_large_image"
        />
        <meta
          name="twitter:title"
          content="VEA Group - Multi-Industry Ecosystem for Business Growth & Sustainability"
        />
        <meta
          name="twitter:description"
          content="VEA Group is your trusted partner for e-commerce, digital marketing, and business growth. Discover our multi-industry ecosystem and innovative solutions."
        />
        <meta name="twitter:image" content="/logo.png" />
      </Helmet>
      <div className="min-h-screen bg-white dark:bg-black text-auto-dark transition-colors duration-300">
        <HomeSlider />
        <AnimatedSection animation={fadeInUp}>
          <StatsSection />
        </AnimatedSection>
        <AnimatedSection animation={fadeInLeft}>
          <AboutSummarySection />
        </AnimatedSection>
        <AnimatedSection animation={fadeInRight}>
          <BrandsSection />
        </AnimatedSection>
        <AnimatedSection animation={fadeInUp}>
          <SubCompaniesSection />
        </AnimatedSection>
        <AnimatedSection animation={fadeInLeft}>
          <BusinessStartSection />
        </AnimatedSection>
        <AnimatedSection animation={fadeInUp}>
          <GlobalPresence />
        </AnimatedSection>
        <AnimatedSection animation={fadeInRight}>
          <ShowcaseSection />
        </AnimatedSection>
        <AnimatedSection animation={fadeInUp}>
          <FeaturedArticlesSection />
        </AnimatedSection>
      </div>
    </>
  );
};

export default Home;
